import React from 'react';
import PropTypes from 'prop-types';
import { Tree, TreeNode } from 'react-organizational-chart';
import {
    Avatar,
    Backdrop,
    Box,
    Card,
    CircularProgress,
    Grid2 as Grid,
    IconButton,
    styled,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import GridLayout from '../../components/GridLayout/GridLayout';
import ZoomableContainer from '../Zoom/Zoom';
import { Close, ExpandLess, ExpandMore } from '@mui/icons-material';
import api from '../../api/api';
import NoData from '../Placeholders/NoData';
import OrganizationSearch from './OrganizationSearch';
import Modal from '@mui/material/Modal';

const StylizedTreeContainer = styled(Box)(() => ({
    width: 'fit-content',
    minWidth: '100%',
    position: 'absolute',
    left: 0,
    right: 0,

}));

const StylizedCard = styled(Card)(({ theme, level, available }) => ({
    alignItems: 'center',
    border: 0,
    textShadow: available === 'true' ? 'none' : '0.5px 0.5px 4px black',
    backgroundColor: available === 'true' ? theme.palette.primary[(level > 4 ? 4 : level) + '00'] : '#DDD',
    display: 'flex',
    margin: '0 auto',
    maxWidth: '170px',
    padding: theme.shape.padding,
    width: '100%',
}));

const StylizedTreeNodeContainer = styled(Box)(() => ({
    alignItems: 'center',
    display: 'inline-flex',
    width: '150px',
}));

const StylizedCardAvatarContainer = styled(Box)(() => ({
    width: '30%',
}));

const StylizedCardAvatar = styled(Avatar)(() => ({
    height: '5vh',
    margin: '0 auto',
    width: '5vh',
}));

const StylizedCardTextContainer = styled(Box)(() => ({
    textAlign: 'right',
    width: '70%',
    wordWrap: 'break-word',
}));    

const Node = ({ data, level, onClick}) => {
    const [expandedNodes, setExpandedNodes] = React.useState({});
    const toggleExpand = (nodeId) => setExpandedNodes(prev => ({ ...prev, [nodeId]: !prev[nodeId], }));

    if (data && data.length > 0) {
        return (
            data.map((child, i) => {
                const expanded = expandedNodes[child.id] ?? false;
                return (
                    <TreeNode
                        key={`TreeNode_${i}`}
                        label={
                            <>
                                {child.parent?.name && <StylizedCard variant="outlined" level={1} available={'true'} style={{ padding: 0 }}>
                                    <div style={{ padding: 10, color: "white", display: 'flex', textAlign: "left", width: '100%', alignItems: 'center', gap: 10, backgroundColor: 'rgba(0,0,0,0.5)' }}>
                                        <StylizedCardAvatarContainer>
                                            <StylizedCardAvatar src={child.parent.profile_picture} style={{width: 30, height: 30}} />
                                        </StylizedCardAvatarContainer>
                                        <StylizedCardTextContainer style={{ textAlign: 'left', width: "100%" }}>
                                            <Typography variant="body2" color="white" fontWeight="bold" whiteSpace="nowrap" textOverflow="ellipsis" overflow="hidden" title={child.parent.name.trim()}>{child.parent.name}</Typography>
                                            <Typography variant="body2" fontSize={10} color="white" whiteSpace="nowrap" textOverflow="ellipsis" overflow="hidden" title={child.parent.position}>{child.parent.position}</Typography>
                                        </StylizedCardTextContainer>
                                    </div>
                                </StylizedCard>}
                                <StylizedCard variant="outlined" level={level} available={child.name.trim() ? 'true' : 'false'} onClick={() => onClick(child)} style={{cursor: "pointer"}}>
                                    <StylizedTreeNodeContainer>
                                        <StylizedCardAvatarContainer>
                                            <StylizedCardAvatar src={child.profile_picture} style={{width: 50, height: 50}} />
                                        </StylizedCardAvatarContainer>
                                        <StylizedCardTextContainer>
                                            <Typography variant="body2" color="white" fontWeight="bold" whiteSpace="nowrap" textOverflow="ellipsis" overflow="hidden" ml={2} title={child.name.trim() || '~Sin asignar'}>{child.name.trim() || '~Sin asignar'}</Typography>
                                            <Typography variant="body2" color="white" whiteSpace="nowrap" textOverflow="ellipsis" overflow="hidden" ml={2} title={child.position}>{child.position}</Typography>
                                            <Typography variant="body2" color="white">{child.location}</Typography>
                                        </StylizedCardTextContainer>
                                    </StylizedTreeNodeContainer>
                                </StylizedCard>
                                {child.children && child.children.length > 0 &&
                                    <IconButton onClick={() => toggleExpand(child.id)}>
                                        {expanded ? <ExpandLess /> : <ExpandMore />}
                                    </IconButton>}
                            </>
                        }>
                        {expanded ?
                            child.children && child.children.length > 0 && child.children.map((_child, _i) => (
                                <Node key={_i} data={[{ ..._child }]} level={level + 1} onClick={onClick}/>
                            ))
                            :
                            null}
                    </TreeNode>
                )
            })
        );
    }

    return (<></>);
};

Node.propTypes = {
    name: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    children: PropTypes.array,
};

const Organization = () => {
    const [lastUpdate, setLastUpdate] = React.useState(null);
    const [data, setData] = React.useState({});
    const [originalData, setOriginalData] = React.useState({})
    const [expanded, setExpanded] = React.useState(true);
    const [loading, setLoading] = React.useState(true);
    const toggleExpand = () => setExpanded(prev => !prev);
    const [userSelected, setUserSelected] = React.useState(null)
    const [open, setOpen] = React.useState(false)

    const theme = useTheme();
    const isLgUp = useMediaQuery(theme.breakpoints.up('lg'));

    React.useEffect(() => {
        const fetchData = async () => {
            try {
    
                const { ok, data, last_update,  } = await api.get('organization');
                if (ok) {
                    setData(data);
                    setOriginalData(data)
                    setLastUpdate(last_update); 
                }
            }
            catch (error) {
                console.log('Error al cargar la información:', error);
            }
            finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    if (loading) return (<Backdrop open={loading} invisible><CircularProgress /></Backdrop>);

    const handleChange = (data) => {
        setData(data)
    }

    const setUser = (node) => {
        setUserSelected(node);
        setOpen(true)
    }

    const handleClose = () => {
        setOpen(false)
    }

    return (
        <>
            <GridLayout style={{ overflow: 'hidden', padding: 0 }}>
                <Grid size={12}>
                    <div style={{ zIndex:30, position: 'relative', padding: 10, backgroundColor: 'white', width: "100%", borderRadius: 8, display:"flex", justifyContent: "space-between", alignItems: "center" }}>
                        <div>
                            <Typography variant="h6" fontWeight="bold" color="primary">ORGANIGRAMA</Typography>
                            {lastUpdate && (
                                <p style={{ margin: 0, fontSize: "0.8em" }}>
                                    Última Actualización: {new Date(lastUpdate).toLocaleDateString('es-ES',{
                                        weekday:'long', day: '2-digit', month:'long', year:'numeric'
                                    })}
                                </p>
                            )}
                        </div>
                        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                            <OrganizationSearch originalData={originalData} onSelectTree={handleChange} />
                        </div>
                    </div>
                    {data && Object.keys(data).length > 0 ?
                        <ZoomableContainer isLargeUpScreen={isLgUp} theme={theme} >
                            <StylizedTreeContainer style={{display: "flex", alignItems: 'center', height: "100vh", width: "100%", justifyContent: "center", minHeight: "100vh"}}>
                                <Tree
                                    lineWidth={expanded ? '1px' : '0px'}
                                    lineColor={theme.palette.secondary.light}
                                    lineBorderRadius={0}
                                    label={
                                        <>
                                            {data.parent?.name && 
                                                <div>
                                                    <StylizedCard variant="outlined" level={1} available={'true'} style={{ padding: 0 }}>
                                                        <div style={{ padding: 10, color: "white", display: 'flex', textAlign: "left", width: '100%', alignItems: 'center', gap: 10, backgroundColor: 'rgba(0,0,0,0.5)' }}>
                                                            <StylizedCardAvatarContainer>
                                                                <StylizedCardAvatar src={data.parent.profile_picture} style={{width: 30, height: 30}} />
                                                            </StylizedCardAvatarContainer>
                                                            <StylizedCardTextContainer style={{ textAlign: 'left', width: "100%" }}>
                                                                <Typography variant="body2" color="white" fontWeight="bold" whiteSpace="nowrap" textOverflow="ellipsis" overflow="hidden" title={data.parent.name.trim()}>{data.parent.name}</Typography>
                                                                <Typography variant="body2" fontSize={10} color="white" whiteSpace="nowrap" textOverflow="ellipsis" overflow="hidden" title={data.parent.position}>{data.parent.position}</Typography>
                                                            </StylizedCardTextContainer>
                                                        </div>
                                                    </StylizedCard>
                                                    <div style={{ width: 1, backgroundColor: 'black', height: 30, margin: 'auto' }} />
                                                </div>
                                            }
                                            <StylizedCard variant="outlined" level={1} available={'true'} onClick={() => setUser(data)} style={{ cursor: "pointer" }}>
                                                <StylizedTreeNodeContainer>
                                                    <StylizedCardAvatarContainer>
                                                        <StylizedCardAvatar src={data.profile_picture} style={{width: 50, height: 50}} />
                                                    </StylizedCardAvatarContainer>
                                                    <StylizedCardTextContainer>
                                                        <Typography variant="body2" color="white" fontWeight="bold" whiteSpace="nowrap" textOverflow="ellipsis" overflow="hidden" ml={2} title={data.name.trim() || '~Sin asignar'}>{data.name || '~Sin asignar'}</Typography>
                                                        <Typography variant="body2" color="white" whiteSpace="nowrap" textOverflow="ellipsis" overflow="hidden" ml={2} title={data.position}>{data.position}</Typography>
                                                        <Typography variant="body2" color="white">{data.location}</Typography>
                                                    </StylizedCardTextContainer>
                                                </StylizedTreeNodeContainer>
                                            </StylizedCard>
                                            {data.children.length > 0 && (
                                                <IconButton onClick={toggleExpand}>
                                                    {expanded ? <ExpandLess /> : <ExpandMore />}
                                                </IconButton>
                                            )}
                                        </>
                                    }>
                                    {expanded && <Node data={data.children} level={2} onClick={setUser} />}
                                </Tree>
                            </StylizedTreeContainer>
                        </ZoomableContainer>
                        :
                        <NoData />}
                </Grid>
            </GridLayout>
            {
                userSelected && (

                    <Modal
                    open={open}
                    onClose={handleClose}
                    aria-labelledby="parent-modal-title"
                    aria-describedby="parent-modal-description">
                        <Box sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            width: 500,
                            bgcolor: 'background.paper',
                            boxShadow: 24,
                            pt: 2,
                            px: 0,
                            pb: 3,
                            borderRadius: .8
                        }}>
                            <div style={{ paddingLeft: 20, paddingRight: 20 }}>
                                <div style={{ display: 'flex', gap: 10, alignItems: "center", marginBottom: 40 }}>
                                    <img src={userSelected.profile_picture} width={60} height={60} style={{ borderRadius: "100%" }} alt={userSelected.full_name} />
                                    <div>
                                        <h3 style={{ margin: 0 }}>{userSelected.full_name}</h3>
                                        <p style={{ margin: 0, fontSize: ".9em" }}>{userSelected.position}</p>
                                    </div>
                                    <IconButton onClick={handleClose} style={{ marginLeft: 'auto' }}><Close /></IconButton>
                                </div>
                                <h4 style={{ marginBottom: 5 }}>Información de contacto</h4>
                                <div>
                                    <p style={{ margin: 0, display: 'flex' }}><span style={{ color: "#ADADAD", minWidth:150, width: 150, display: 'inline-block' }}>Oficina / Nave</span> {userSelected.full_location}</p>
                                    <p style={{ margin: 0, display: 'flex' }}><span style={{ color: "#ADADAD", minWidth:150, width: 150, display: 'inline-block' }}>Email</span> <a style={{ color: "#174F91" }} href={`mailto:${userSelected.email}`} >{userSelected.email}</a></p>
                                    {userSelected.phone && <p style={{ margin: 0, display: 'flex' }}><span style={{ color: "#ADADAD", minWidth:150, width: 150, display: 'inline-block' }}>Teléfono celular</span> <a style={{ color: "#174F91" }} href={`tel:${userSelected.phone}`} >{userSelected.phone}</a></p>}
                                </div>
                                <h4 style={{ marginBottom: 5 }}>Información laboral</h4>
                                <div>
                                    <p style={{ margin: 0, display: 'flex' }}><span style={{ color: "#ADADAD", minWidth:150, width: 150, display: 'inline-block' }}>Puesto</span> {userSelected.position}</p>
                                    <p style={{ margin: 0, display: 'flex' }}><span style={{ color: "#ADADAD", minWidth:150, width: 150, display: 'inline-block' }}>Departamento</span> {userSelected.full_department}</p>
                                    <p style={{ margin: 0, display: 'flex' }}><span style={{ color: "#ADADAD", minWidth:150, width: 150, display: 'inline-block' }}>Área</span> {userSelected.area}</p>
                                </div>
                            </div>
                        </Box>
                    </Modal>
                )
            }
        </>
    );
};

Organization.propTypes = {
    data: PropTypes.shape({
        name: PropTypes.string.isRequired,
        title: PropTypes.string.isRequired,
        children: PropTypes.array,
    }).isRequired,
};

export default Organization;